import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TasksService } from 'src/app/data/service/tasks.service';

/** One row of the picker: a person, or a role written as 'role:<key>'. */
export interface PersonOption {
  id: string;
  name: string;
  email?: string;
  isRole?: boolean;
}

/**
 * Choose who a task goes to: the organisation's people from api/tasks/assignees, drawn as the
 * dialog kit's sd-picker with an initials avatar per person. The value is a user id, or
 * 'role:admin' when [includeRoles] offers "Any admin".
 */
@Component({
  selector: 'app-person-picker',
  templateUrl: './person-picker.component.html',
  styleUrls: ['./person-picker.component.scss']
})
export class PersonPickerComponent implements OnChanges {
  @Input() organizationId: string;
  @Input() value: string = null;
  @Output() valueChange = new EventEmitter<string>();
  /** Offer the organisation's roles ("Any admin") as well as people. */
  @Input() includeRoles = false;
  /** Leave someone out, e.g. whoever the task is already with. */
  @Input() excludeUserId: string;
  @Input() placeholder = 'Search people';
  @Input() labelForId: string;
  @Input() invalid = false;

  options: PersonOption[] = [];
  loading = false;
  private lastLoaded: { users: PersonOption[]; roles: PersonOption[] } = { users: [], roles: [] };

  constructor(private _tasks: TasksService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.organizationId && this.organizationId) {
      this.load();
    } else if (changes.includeRoles || changes.excludeUserId) {
      this.options = this.build(this.lastLoaded);
    }
  }

  private load(): void {
    this.loading = true;
    this._tasks.assignees(this.organizationId).subscribe(
      res => {
        this.lastLoaded = {
          users: (res?.users || []).map(u => ({ id: u.id, name: u.name || u.email, email: u.email })),
          roles: (res?.roles || []).map(r => ({ id: 'role:' + r.key, name: r.name, isRole: true }))
        };
        this.options = this.build(this.lastLoaded);
        this.loading = false;
      },
      () => {
        this.options = [];
        this.loading = false;
      });
  }

  private build(loaded: { users: PersonOption[]; roles: PersonOption[] }): PersonOption[] {
    const exclude = (this.excludeUserId || '').toLowerCase();
    const users = loaded.users
      .filter(u => !exclude || (u.id || '').toLowerCase() !== exclude)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return this.includeRoles ? [...loaded.roles, ...users] : users;
  }

  /** Search by name or email. */
  match = (term: string, item: PersonOption): boolean => {
    const t = (term || '').trim().toLowerCase();
    return !t || (item.name || '').toLowerCase().includes(t) || (item.email || '').toLowerCase().includes(t);
  }

  onChange(value: string): void {
    this.value = value;
    this.valueChange.emit(value);
  }
}
